import styled from 'styled-components';
import { Text } from 'foundations-components/components/Text';
import { Link } from 'foundations-components/transitional/components/Link';
import { View } from 'foundations-components/components/View';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useRef } from 'react';
import { currentOrderedProductLevels } from 'self-service-api/constants/ProductLevels';
import withErrorBoundary from '../ErrorBoundary';
import { FEATURE_TABLE } from '../_core/common/constants/ErrorBoundaryNames';
import { tracker } from '../_core/common/eventTracking/tracker';
import { useIntersectionObserver } from '../_core/common/hooks/useIntersectionObserver';
import { getProductLevelFromApiNameString } from '../_core/features/FeaturesUtils';
import ExpandableSection from './ExpandableSection';
import FeatureTableHead from './FeatureTableHead';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const TableContainer = styled.div.withConfig({
  displayName: "FeatureTable__TableContainer",
  componentId: "jqupg6-0"
})(["width:100%;"]);
export const Table = styled.table.withConfig({
  displayName: "FeatureTable__Table",
  componentId: "jqupg6-1"
})(({
  theme
}) => ({
  width: '100%',
  borderSpacing: theme.borderWidth['0'],
  tableLayout: 'fixed'
}));
const FeatureTable = ({
  headerData,
  bodyData,
  canViewNewSeats,
  onExpand,
  onCollapse,
  defaultNumberOfRowsToShow,
  isInbound25PricingPage = false
}) => {
  const sections = Object.keys(bodyData.sectionData);
  const productLevels = new Set();
  sections.forEach(section => {
    bodyData.sectionData[section].apiNames.forEach(apiName => {
      productLevels.add(getProductLevelFromApiNameString(apiName));
    });
  });

  // make sure the productLevels are sorted FREE, STARTER, PROFESSIONAL, ENTERPRISE
  const sortedProductLevels = Array.from(productLevels).sort((a, b) => {
    return currentOrderedProductLevels.indexOf(a) - currentOrderedProductLevels.indexOf(b);
  });
  const featureTableRef = useRef(null);
  useIntersectionObserver({
    onIntersect: () => {
      tracker.track('featureTableInteraction', {
        action: 'scrolled-in-view',
        screen: bodyData.sharedData.subscreen
      });
    },
    targetRef: featureTableRef
  });
  const handleProductAndServicesCatalogClick = () => {
    tracker.track('featureTableInteraction', {
      action: 'products-services-catalog-click',
      screen: bodyData.sharedData.subscreen
    });
  };
  return /*#__PURE__*/_jsxs(TableContainer, {
    "data-test-id": "feature-table",
    id: "feature-table",
    ref: featureTableRef,
    children: [/*#__PURE__*/_jsxs(Table, {
      children: [/*#__PURE__*/_jsx(FeatureTableHead, {
        headerData: headerData,
        isInbound25PricingPage: isInbound25PricingPage
      }), sections.map(section => {
        return /*#__PURE__*/_jsx(ExpandableSection, {
          featureTableCategory: bodyData.sectionData[section],
          tableSharedData: bodyData.sharedData,
          id: section,
          showLabel: sections.length > 1,
          canViewNewSeats: canViewNewSeats,
          onExpand: onExpand,
          onCollapse: onCollapse,
          defaultNumberOfRowsToShow: defaultNumberOfRowsToShow,
          isInbound25PricingPage: isInbound25PricingPage,
          productLevels: sortedProductLevels
        }, section);
      })]
    }), /*#__PURE__*/_jsx(View, {
      minBlockSize: "48px",
      children: /*#__PURE__*/_jsx(Text, {
        typography: "body-100",
        paddingBlockEnd: "200",
        textAlign: "left",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "upgrades.featureTable.productServicesCatalogPrompt.prompt",
          options: {
            link: /*#__PURE__*/_jsx(Link, {
              href: "https://legal.hubspot.com/hubspot-product-and-services-catalog",
              external: true,
              onClick: handleProductAndServicesCatalogClick,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "upgrades.featureTable.productServicesCatalogPrompt.linkText"
              })
            })
          }
        })
      })
    })]
  });
};
export default withErrorBoundary(FeatureTable, {
  boundaryName: FEATURE_TABLE
});